import React from 'react'
import { reportsService } from 'app/modules/reports/services'
import { defaultTimeout, NotificationData } from 'app/modules/shared/context/main-notification-context'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import { ReportFormat } from 'app/modules/user/features/downloads/types'
import { Link } from 'react-router-dom'
import { getPageUrl } from 'app/modules/shared/routing'
import { ReportExportProfileOptions } from 'app/modules/user/features/profile/types'

export async function handleReportDownload(
  reportId: string,
  format: ReportFormat,
  hash: string,
  flowId: string,
  mainNotification: NotificationData,
  setDownloading: (state: boolean) => void,
  _t: CallableFunction,
  exportOptions?: ReportExportProfileOptions
) {
  const isFastDownload = detectFastDownload(format as ReportFormat)
  const filename = buildExportFileName(isFastDownload, hash, flowId, format as ReportFormat)

  // Send initial request for report download in case of "slow" formats - html or pdf
  async function startSlowDownload() {
    showDownloadStartedNotification(mainNotification, format as ReportFormat, _t)

    try {
      const data = await reportsService.startSlowDownloadReport(reportId as string, format, exportOptions)
      return data.export_id
    } catch (error) {
      setDownloading(false)
      console.error(error)
      mainNotification.setMessageData(_t('report-download-error'), 'danger', isFastDownload ? defaultTimeout : 0)
      return
    }
  }

  // Check progress of "slow" download or immediately receive content for "fast" download
  async function handle(exportId: string | undefined) {
    try {
      const [data, _, code] = await reportsService.downloadReport(reportId as string, exportId, format)
      const result = processReponse(data, code, format, filename, mainNotification, _t, isFastDownload)

      if (result) {
        clearTimeout(timeout)
        setDownloading(false)
        return
      }
    } catch (error) {
      clearTimeout(timeout)
      setDownloading(false)
      console.error(error)
      mainNotification.setMessageData(_t('report-download-error'), 'danger', isFastDownload ? defaultTimeout : 0)
      return
    }

    timeout = setTimeout(() => {
      handle(exportId)
    }, 3000)
  }

  const exportId = isFastDownload ? undefined : await startSlowDownload()

  let timeout = setTimeout(() => {
    handle(exportId)
  }, 0)
}

function processReponse(
  data: string | undefined,
  code: number,
  format: string,
  filename: string,
  mainNotification: NotificationData,
  _t: CallableFunction,
  isFastDownload: boolean
) {
  format = format.toUpperCase()

  // We got report content
  if (code === 200) {
    const objectUrl = downloadReportContent(data as string, filename)

    const message = isFastDownload ? (
      _t('report-downloaded', { format })
    ) : (
      <div className="d-flex flex-column">
        <div>
          {_t('report-downloaded', { format })}
          <br />
          {_t('you-can-get-it-by-this-link')}:
        </div>
        <div>
          <DownloadLink objectUrl={objectUrl} filename={filename} format={format} />
        </div>
      </div>
    )

    mainNotification.setMessageData(message, 'success', isFastDownload ? defaultTimeout : 0)

    return true
  }

  // Here code === 204
  // Generation in progress, need to wait
  return false
}

function showDownloadStartedNotification(
  mainNotification: NotificationData,
  format: ReportFormat,
  _t: CallableFunction
) {
  const message = (
    <div className="d-flex flex-column">
      <div>
        {_t('report-generation-started', { format: format.toUpperCase() })}
        <br />
        {_t('automatic-download-attempt')}
        <br />
        {_t('find-file-on-my-downloads')}:
      </div>
      <div>
        <Link to={getPageUrl('my-downloads') as string} className="lonely-link d-block mt-2 capitalize">
          {_t('my-downloads')}
        </Link>
      </div>
    </div>
  )

  mainNotification.setMessageData(message, 'info', 0)
}

export function detectFastDownload(format: ReportFormat) {
  return [ReportFormat.Misp, ReportFormat.Stix, ReportFormat.Raw].includes(format)
}

export function buildExportFileName(isFastDownload: boolean, hash: string, flowId: string, format: ReportFormat) {
  return isFastDownload ? `${hash}-${flowId}.${format}.json` : `${hash}-${flowId}.${format}`
}

export function downloadReportContent(data: string, filename: string) {
  const link = window.URL.createObjectURL(new Blob([data]))
  const elem = document.createElement('a')
  elem.href = link
  elem.setAttribute('download', filename)
  document.body.appendChild(elem)
  elem.click()
  document.body.removeChild(elem)

  return link
}

function DownloadLink({ objectUrl, format, filename }: { objectUrl: string; format: string; filename: string }) {
  const { _t } = useTypedTranslation()

  return (
    <a href={objectUrl} download={filename} className="lonely-link d-block mt-2">
      {_t('report-of-format', { format })}
    </a>
  )
}
